<template>
  <div>
    <FormSection
      :title="$t('form.invoiceForm.headers.items')"
      :image="require('~/images/ico-bill.svg')"
      class="invoice-items"
      :class="{ dragged: drag }"
      is-new
    >
      <FormGroup
        radio
        prop="vat_calculation"
        :label="$t('form.invoiceForm.fields.vat_calculation.label')"
        v-if="visibilities.vat_calculation"
        slot="control"
        spacing="xl"
        :inline-form="true"
        class="vat-calculation"
      >
        <RadioGroup
          v-model="vat_calculation"
          data-test="vat_calculation"
          class="radio-select"
        >
          <Radio
            v-for="radio in vatOptions"
            :label="radio.key"
            :key="`vat_calculations_${radio.key}`"
            :data-test="`${radio.key}_set`"
          >
            {{ radio.value }}
          </Radio>
        </RadioGroup>
      </FormGroup>
      <draggable
        v-model="sortedList"
        handle=".handle"
        v-bind="dragOptions"
        @start="drag = true"
        @end="drag = false"
      >
        <div
          v-for="(line, index) in lines"
          :key="'invoice_lines_wrapper_' + index"
          data-test="line_wrapper"
          class="invoice-line-wrapper"
        >
          <InvoiceLine
            :can-delete-line="canDeleteLines"
            :class="{ 'first-item': index === 0 }"
            :drag="drag"
            :index="index"
            :is-last-line="isLastLine(line)"
            :key="'invoice_lines_' + index"
            :vat-rate-options="vatRateOptions"
            :vat-rate-select-hidden="vatRateSelectHidden"
            @addInvoiceItem="addInvoiceItem"
            @duplicateInvoiceItem="duplicateInvoiceItem"
            v-if="line.is_discount_line || line.is_discount_line === null"
          />
          <InvoiceDiscountLine
            v-if="line.is_discount_line && !line._destroy"
            discount-type="line"
            :index="index"
            :vat-rate-select-hidden="vatRateSelectHidden"
            :can-delete-line="canDeleteLines"
            data-test="line_discount"
          />
        </div>
      </draggable>
      <div class="invoice-item-buttons">
        <Button
          type="secondary"
          size="medium"
          icon="icon icon-add-3"
          @click="addInvoiceItem"
          data-test="add_invoice_item"
          class="add-item-button button-mobile"
        >
          {{ $t("form.invoiceForm.buttons.addItem") }}
        </Button>
        <Button
          type="no-border"
          v-if="isAuthorized && savedLinesEnabled"
          icon="icon icon-heart-favorites"
          @click="addSavedItem"
          data-test="add_saved_item"
          class="add-saved-item-button button-mobile"
        >
          {{ $t("form.invoiceForm.buttons.addSavedItem") }}
        </Button>
      </div>
    </FormSection>
    <SavedLinesDialog
      :vat-rate-select-hidden="vatRateSelectHidden"
    />
    <DiscountDialog />
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { mapState, mapMutations, mapGetters } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import {
  INVOICE_KIND_NO_VAT,
  INVOICE_KIND_PROFORMA,
  INVOICE_KIND_VAT_DUTY_REVERSED,
} from '~/config/constants';

export default {
  components: {
    DiscountDialog: () => import('../../../InvoiceForm/sections/discount/DiscountDialog.vue'),
    InvoiceDiscountLine: () => import('./discount/InvoiceDiscountLine.vue'),
    InvoiceLine: () => import('./InvoiceLine.vue'),
    SavedLinesDialog: () => import('~/components/InvoiceForm/sections/SavedLinesDialog.vue'),
    draggable,
  },
  data() {
    return {
      drag: false,
    };
  },
  computed: {
    ...mapState('invoiceForm', [
      'visibilities',
      'vat_calculation',
      'seller_address_country_code',
      'kind',
      'lines',
    ]),
    ...mapFields('invoiceForm', ['vat_calculation']),
    ...mapState('config', ['global']),
    ...mapState('config', {
      vatOptions: state => state.invoice.vats,
      savedLinesEnabled: state => state.global.saved_lines,
    }),
    ...mapGetters('auth', ['isAuthorized']),
    ...mapGetters('invoiceForm', ['sortedLines']),
    vatRateOptions() {
      return this.getVatRateOption();
    },
    vatRateSelectHidden() {
      if (this.kind === INVOICE_KIND_VAT_DUTY_REVERSED) {
        return false;
      }
      return !this.visibilities.vat_calculation;
    },
    canDeleteLines() {
      return this.lines.filter(item => item._destroy !== true).length > 1;
    },
    sortedList: {
      get() {
        return this.sortedLines;
      },
      set(object) {
        const newSort = object.map((row, index) => ({
          identifier: row.identifier,
          position: index,
        }));

        this.$store.commit('invoiceForm/updateLinesPositions', newSort);
      },
    },
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost',
      };
    },
  },
  methods: {
    ...mapMutations('invoiceForm', ['setZeroVatRates']),
    addInvoiceItem() {
      this.$store.dispatch('invoiceForm/addInvoiceItem');
    },
    duplicateInvoiceItem(index) {
      this.$store.dispatch('invoiceForm/duplicateInvoiceItem', index);
    },
    addSavedItem() {
      this.$store.dispatch('savedLines/openDialog');
    },
    getVatRateOption() {
      const countryConfig = this.global.vat_schemes.filter(
        obj => obj.code === this.seller_address_country_code,
      )[0];

      let rates = [0];

      if (countryConfig && countryConfig.current_rates) {
        rates = rates.concat(countryConfig.current_rates.map(key => key.rate));
      }

      return rates;
    },
    isLastLine(line) {
      const visibleLines = this.lines.filter(item => item._destroy !== true);
      return line === visibleLines[visibleLines.length - 1];
    },
  },
  watch: {
    kind(val) {
      const kinds = [INVOICE_KIND_NO_VAT, INVOICE_KIND_PROFORMA];
      if (kinds.includes(val)) {
        this.setZeroVatRates();
      }

      if (val === INVOICE_KIND_VAT_DUTY_REVERSED) {
        this.$store.dispatch('invoiceForm/updateStoreValue', {
          name: 'vat_calculation',
          value: 'vat_inclusive',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.radio-select {
  margin-top: 2px;

  :deep() {
    .el-radio {
      margin-right: 10px;
    }
  }
}

.invoice-items {
  :deep() {
    .on-right {
      width: 50%;

      @include media('<sm') {
        width: 100%;
        margin-top: rem(5px);
      }
    }

    .new-form-enabled & {
      @include media('<sm') {
        flex-direction: column;
        align-items: flex-start;
      }
    }
  }
}

.vat-calculation {
  margin-bottom: 0;

  @include media('<sm') {
    justify-content: flex-start !important;
  }

  :deep() {
    * {
      font-weight: 400 !important;
    }

    justify-content: flex-end;

    .el-form-item__content {
      width: auto;
      flex-basis: auto;

      @media only screen and (max-width: $--breakpoint-sm - 1px) {
        padding-top: 0;
      }
    }
    .el-form-item__label {
      width: auto;
      text-align: right;
      flex-basis: auto;
      max-width: 100%;
    }
  }
}

.invoice-item-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: rem(20) rem(10);
  margin-top: .5rem;

  @media only screen and (max-width: $--breakpoint-sm - 1px) {
    .el-button--secondary {
      width: 100%;
    }
    .add-saved-item-button {
      margin-left: 0;
    }
  }
}

.invoice-item-buttons .el-button + .el-button {
  margin-left: 0;
}

@media only screen and (max-width: $--breakpoint-md) {
  .invoice-items-header {
    display: none;
  }
}

.ghost {
  opacity: .5;
}

.dragged {
  :deep() {
    .handle {
      transform: none;
    }
  }
}

.invoice-line-wrapper {
  @media only screen and (min-width: $--breakpoint-sm) and (max-width: $--lg - 1) {
    margin-bottom: 2rem;
  }
}

:deep() {
  .invoice-line-wrapper:not(:first-child) {
    @media only screen and (min-width: $--lg) {
      .el-input__label {
        display: none;
      }
    }
  }
}
</style>
